// src/lib/resa-tombstones.js — helpers purs pour saveResa/deleteResa
// (fix iCal modifications manuelles 2026-05-23).
//
// Extraits de App.jsx pour testabilité Jest. Logique identique aux call-sites
// dans App.jsx :: saveResa / deleteResa. Voir aussi src/lib/channels/ical.js
// :: getTombstones pour la lecture côté sync iCal.
//
// Structure tombstones globale par logement (M-3) :
//   data._tombstones[lid] = {
//     ical_uids:       [{uid, deletedAt, reason}],
//     legacy_checkins: [{checkIn, deletedAt, reason}],
//     airbnb_codes:    [{airbnbCode, deletedAt, reason}],
//     booking_refs:    [{bookingRef, deletedAt, reason}],
//   }

// Initialise (si absent) la structure tombstones et renvoie sa référence.
// Mute `nd` en place — appelant responsable du deep clone préalable.
export function ensureTombstones(nd, lid) {
  if (!nd._tombstones) nd._tombstones = {}
  if (!nd._tombstones[lid]) {
    nd._tombstones[lid] = {
      ical_uids: [],
      legacy_checkins: [],
      airbnb_codes: [],
      booking_refs: [],
    }
  } else {
    // Rétrocompat : compléter les buckets manquants
    const buckets = ['ical_uids', 'legacy_checkins', 'airbnb_codes', 'booking_refs']
    buckets.forEach(b => {
      if (!Array.isArray(nd._tombstones[lid][b])) nd._tombstones[lid][b] = []
    })
  }
  return nd._tombstones[lid]
}

// Pose les tombstones multi-canaux pour une résa supprimée. Anti-doublon par
// identifiant. Mute `nd` (clone géré par l'appelant).
//
// resaToDelete peut être null (rien à faire) — fonction défensive.
export function pushTombstonesForResa(nd, lid, resaToDelete) {
  if (!resaToDelete) return
  const tomb = ensureTombstones(nd, lid)
  const nowIso = new Date().toISOString()

  // Tombstone UID iCal (path principal)
  if (resaToDelete.ical_uid && !tomb.ical_uids.some(t => t.uid === resaToDelete.ical_uid)) {
    tomb.ical_uids.push({ uid: resaToDelete.ical_uid, deletedAt: nowIso, reason: null })
  }
  // Tombstone date legacy B-1 (résas sans UID — pré-migration)
  if (!resaToDelete.ical_uid && resaToDelete.checkIn
      && !tomb.legacy_checkins.some(t => t.checkIn === resaToDelete.checkIn)) {
    tomb.legacy_checkins.push({ checkIn: resaToDelete.checkIn, deletedAt: nowIso, reason: null })
  }
  // Tombstone CSV Airbnb B-3
  if (resaToDelete.airbnbCode
      && !tomb.airbnb_codes.some(t => t.airbnbCode === resaToDelete.airbnbCode)) {
    tomb.airbnb_codes.push({ airbnbCode: resaToDelete.airbnbCode, deletedAt: nowIso, reason: null })
  }
  // Tombstone CSV Booking B-3
  if (resaToDelete.bookingRef
      && !tomb.booking_refs.some(t => t.bookingRef === resaToDelete.bookingRef)) {
    tomb.booking_refs.push({ bookingRef: resaToDelete.bookingRef, deletedAt: nowIso, reason: null })
  }
}

// ── Lot 2 V2-delete-resa — suppression d'une résa V2 par CODE STABLE seul ──
// Pose UNIQUEMENT airbnb_codes / booking_refs (PAS de legacy_checkin — décision
// M2 : la suppression V2 cible un code stable réappliqué par la projection à
// chaque render, tous les mois). Anti-doublon par identifiant. reason:'v2_delete'.
// Mute `nd` (clone géré par l'appelant). resa null → no-op défensif.
//
// EFFET DOUBLE ASSUMÉ (M2) : ce code sera AUSSI vu par la sync iCal via
// getTombstones (mêmes buckets) → la résa ne sera pas réimportée. Documenté +
// testé (test croisé getTombstones dans resa-tombstones.test.js).
export function pushStableCodeTombstone(nd, lid, resa) {
  if (!resa) return
  const code = resa.airbnbCode || resa.bookingRef || null
  if (!code) return // garde : pas de code stable → rien à tombstoner
  const tomb = ensureTombstones(nd, lid)
  const nowIso = new Date().toISOString()
  if (resa.airbnbCode
      && !tomb.airbnb_codes.some(t => t.airbnbCode === resa.airbnbCode)) {
    tomb.airbnb_codes.push({ airbnbCode: resa.airbnbCode, deletedAt: nowIso, reason: 'v2_delete' })
  }
  if (resa.bookingRef
      && !tomb.booking_refs.some(t => t.bookingRef === resa.bookingRef)) {
    tomb.booking_refs.push({ bookingRef: resa.bookingRef, deletedAt: nowIso, reason: 'v2_delete' })
  }
}

// Retire le tombstone de code stable d'une résa (RESTAURATION V2). Retire des
// buckets airbnb_codes / booking_refs l'entrée dont le code matche resa.airbnbCode
// || resa.bookingRef. Mute `nd` (clone géré par l'appelant). Défensif : nd sans
// _tombstones / lid absent / resa sans code → no-op.
export function removeStableCodeTombstone(nd, lid, resa) {
  if (!resa) return
  const t = nd && nd._tombstones && nd._tombstones[lid]
  if (!t) return
  if (resa.airbnbCode && Array.isArray(t.airbnb_codes)) {
    t.airbnb_codes = t.airbnb_codes.filter(e => e.airbnbCode !== resa.airbnbCode)
  }
  if (resa.bookingRef && Array.isArray(t.booking_refs)) {
    t.booking_refs = t.booking_refs.filter(e => e.bookingRef !== resa.bookingRef)
  }
}

// Pose manual_origin: true + préserve ical_uid + ical_sync_log existants.
// Retourne la résa enrichie. M-4 : log un warn si airbnbCode/bookingRef
// présent sans ical_uid (possible state stale ou résa pré-fix).
//
// `existing` peut être null (création) — dans ce cas ical_uid=null,
// ical_sync_log=[].
export function applyManualOriginFlag(resa, existing) {
  const out = { ...resa, manual_origin: true }
  if (existing) {
    out.ical_uid = existing.ical_uid ?? null
    out.ical_sync_log = Array.isArray(existing.ical_sync_log) ? existing.ical_sync_log : []
  } else {
    out.ical_uid = null
    out.ical_sync_log = []
  }
  // M-4 — observabilité
  if (!out.ical_uid && (out.airbnbCode || out.bookingRef)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[saveResa] résa sans ical_uid alors qu\'un airbnbCode/bookingRef existe — possible state React stale ou résa pré-fix',
      { resaId: resa.id, airbnbCode: out.airbnbCode || null, bookingRef: out.bookingRef || null },
    )
  }
  return out
}
